/**
 * Pure geometry and scoring for Perfect Circle, kept out of the component so it
 * can be reasoned about (and tested) without a canvas.
 */

/** Ignore samples this close to the centre dot — they make the radius explode. */
export const MIN_RADIUS = 24;

/** A loop counts as closed slightly before a full turn so you aren't punished
 * for letting go a few pixels early. */
export const FULL_TURN = Math.PI * 2 - 0.12;

/** Deviation (as a fraction of your mean radius) that reads as fully "off". */
export const MAX_DEVIATION = 0.15;

export type Point = { x: number; y: number; radius: number };

export const meanOf = (values: number[]) =>
  values.reduce((sum, value) => sum + value, 0) / values.length;

/**
 * Score is the coefficient of variation of your radius, doubled so that small
 * wobbles cost real points: a perfectly steady radius is 100%, and a radius
 * that swings ±25% around its own mean bottoms out at 0.
 */
export const scoreOf = (radii: number[]) => {
  if (radii.length === 0) return 0;

  const mean = meanOf(radii);
  if (mean === 0) return 0;

  const variance = meanOf(radii.map((r) => (r - mean) ** 2));
  const coefficient = Math.sqrt(variance) / mean;

  return Math.max(0, Math.min(100, 100 * (1 - coefficient * 2)));
};

/** Green when the segment sits on your mean radius, red when it strays. */
export const colorFor = (radius: number, mean: number) => {
  const deviation = Math.min(Math.abs(radius - mean) / mean, MAX_DEVIATION);
  const hue = 140 - (deviation / MAX_DEVIATION) * 140;

  return `hsl(${hue} 85% 52%)`;
};

/**
 * Shortest signed angular step from `from` to `to`, so a sweep that crosses the
 * ±π seam accumulates smoothly instead of jumping a whole turn.
 */
export const angleDelta = (from: number, to: number) => {
  let delta = to - from;
  if (delta > Math.PI) delta -= Math.PI * 2;
  if (delta < -Math.PI) delta += Math.PI * 2;

  return delta;
};

export const verdictFor = (accuracy: number) => {
  if (accuracy >= 95) return 'Suspiciously good.';
  if (accuracy >= 88) return 'A respectable circle.';
  if (accuracy >= 75) return 'That is more of an oval.';

  return 'That is a potato.';
};
